import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Chip,
  Box,
  Stack,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Check as CheckIcon,
  Toll as CreditIcon,
  Visibility as PreviewIcon,
} from '@mui/icons-material';
import type { ThemeWithOwnership } from '../../types';
import { THEME_CATEGORIES } from '../../types';
import { ThemePreview } from './ThemePreview';
import { ThemeFullPreview } from './ThemeFullPreview';

interface ThemeCardProps {
  theme: ThemeWithOwnership;
  onPurchase?: (theme: ThemeWithOwnership) => void;
  onSelect?: (theme: ThemeWithOwnership) => void;
  isPurchasing?: boolean;
  showActionButton?: boolean;
  /** Show preview button to open full preview dialog */
  showPreviewButton?: boolean;
}

// Get display label for category
const getCategoryLabel = (category: string): string => {
  const found = THEME_CATEGORIES.find(c => c.value === category);
  return found?.label || category;
};

export const ThemeCard: React.FC<ThemeCardProps> = ({
  theme,
  onPurchase,
  onSelect,
  isPurchasing = false,
  showActionButton = true,
  showPreviewButton = true,
}) => {
  const isOwned = theme.owned || theme.isFree;
  const [previewOpen, setPreviewOpen] = useState(false);

  const handleAction = () => {
    if (isOwned && onSelect) {
      onSelect(theme);
    } else if (!isOwned && onPurchase) {
      onPurchase(theme);
    }
  };

  return (
    <>
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
      }}
    >
      {/* Preview button */}
      {showPreviewButton && (
        <Tooltip title="Preview theme">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setPreviewOpen(true);
            }}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              zIndex: 1,
              bgcolor: 'background.paper',
              boxShadow: 1,
              '&:hover': { bgcolor: 'grey.100' },
            }}
          >
            <PreviewIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}

      <CardContent sx={{ flex: 1, pb: 1 }}>
        {/* Theme Preview */}
        <Box sx={{ mb: 2 }}>
          <ThemePreview
            theme={theme}
            size="medium"
            showOwnershipBadge={false}
            hideInfo
            sx={{ border: 0, p: 0, bgcolor: 'transparent' }}
          />
        </Box>

        {/* Theme Name & Category */}
        <Typography variant="h6" gutterBottom noWrap>
          {theme.name}
        </Typography>

        <Stack direction="row" spacing={0.5} mb={1}>
          <Chip
            label={getCategoryLabel(theme.category)}
            size="small"
            variant="outlined"
          />
          {theme.isFree && (
            <Chip label="Free" size="small" color="success" />
          )}
          {theme.owned && !theme.isFree && (
            <Chip
              icon={<CheckIcon sx={{ fontSize: '1rem !important' }} />}
              label="Owned"
              size="small"
              color="primary"
            />
          )}
        </Stack>

        {/* Description */}
        {theme.description && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {theme.description}
          </Typography>
        )}
      </CardContent>

      {showActionButton && (
        <CardActions sx={{ p: 2, pt: 0 }}>
          {isOwned ? (
            onSelect ? (
              <Button
                fullWidth
                variant="outlined"
                color="primary"
                onClick={handleAction}
              >
                Use Theme
              </Button>
            ) : (
              <Button
                fullWidth
                variant="outlined"
                color="success"
                startIcon={<CheckIcon />}
                disabled
              >
                Owned
              </Button>
            )
          ) : (
            <Button
              fullWidth
              variant="contained"
              color="primary"
              startIcon={<CreditIcon />}
              onClick={handleAction}
              disabled={isPurchasing || !onPurchase}
            >
              {isPurchasing ? 'Processing...' : theme.creditPrice ? `${theme.creditPrice} Credit${theme.creditPrice !== 1 ? 's' : ''}` : 'Purchase'}
            </Button>
          )}
        </CardActions>
      )}
    </Card>

    {/* Full Preview Dialog */}
    <ThemeFullPreview
      theme={theme}
      open={previewOpen}
      onClose={() => setPreviewOpen(false)}
      actions={
        <>
          <Button onClick={() => setPreviewOpen(false)}>Close</Button>
          {showActionButton && (
            isOwned ? (
              onSelect && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    setPreviewOpen(false);
                    handleAction();
                  }}
                >
                  Use Theme
                </Button>
              )
            ) : (
              onPurchase && (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<CreditIcon />}
                  onClick={() => {
                    setPreviewOpen(false);
                    handleAction();
                  }}
                  disabled={isPurchasing}
                >
                  {theme.creditPrice ? `Purchase for ${theme.creditPrice} Credit${theme.creditPrice !== 1 ? 's' : ''}` : 'Purchase'}
                </Button>
              )
            )
          )}
        </>
      }
    />
    </>
  );
};

export default ThemeCard;
